/**
 * ===== BODY REGION UPDATE GUIDELINES =====
 * 
 * OVERVIEW:
 * This application processes medical exam data for various body regions, calculates
 * impairment ratings per AMA Guides, and presents results in a structured format.
 * 
 * UPDATE PROCESS:
 * When implementing a new body region or modifying an existing one, follow these steps:
 * 
 * 1. DATA STRUCTURE
 *    - Update ExamData interface with the new region fields
 *    - Follow the nested object pattern: examData.regionName.field_name
 *    - For example: examData.shoulders.right_flexion
 * 
 * 2. INITIALIZATION
 *    - Update createEmptyExamData() function to initialize the new region
 *    - Update any development/test data in getDevModeTestData() function
 * 
 * 3. BACKEND SCHEMA
 *    - Update the Gemini AI schema in functions/src/index.ts
 *    - Ensure field names match exactly between frontend and backend
 * 
 * 4. UI COMPONENTS
 *    - Create/modify the tab UI with appropriate fields for the region
 *    - Add the region to the tabs array
 *    - Implement any specialized UI components needed
 * 
 * 5. RATING CALCULATIONS
 *    - Create calculation functions following the pattern in calculateFlexion()
 *    - Use anchor points based on AMA Guides for each measurement
 *    - Display Whole Person Impairment (WPI = UE impairment × 0.6)
 * 
 * FIELD NAMING CONVENTIONS:
 * - All measurements should use degrees: right_flexion, left_extension
 * - Boolean flags should use include prefix: right_includeAcromioplasty
 * - All values should permit null for unset values: number | null, boolean | null
 * - Follow side_motion pattern: right_flexion, left_abduction, etc.
 * 
 * SHOULDERS EXAMPLE:
 * - Data structure includes: flexion, extension, abduction, adduction, internal_rotation, external_rotation
 * - Each with right_ and left_ versions
 * - Boolean fields right_includeAcromioplasty and left_includeAcromioplasty
 * - All fields accept null values for unset data
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ResultsSummaryBox from './components/ResultsSummaryBox';
import PhysicalExamBox from './components/PhysicalExamBox';
import AuthModal from './components/AuthModal';
import UserHeader from './components/UserHeader';
import { useAuth, manualSessionActive } from './hooks/useAuth';
import './App.css';
import { calculateHandWPI } from './utils/handCalculations';
import { combine } from './utils/amaMath';
import { ExamData, NORMAL_ROM_VALUES, initializeExamData, updateExamField, HAND_DIGITS, fillHandNormals } from './utils/examData';
import {
  calculateGripStrengthImpairment,
  calculateFlexion, calculateExtension, calculateAbduction, calculateAdduction,
  calculateInternalRotation, calculateExternalRotation,
  calculateElbowFlexion, calculateElbowExtension, calculatePronation, calculateSupination,
  calculateWristFlexion, calculateWristExtension, calculateWristRadialDeviation, calculateWristUlnarDeviation,
  calculateHipFlexion, calculateHipExtensionContracture, calculateHipInternalRotation,
  calculateHipExternalRotation, calculateHipAdduction, calculateHipAbductionContracture,
} from './components/TabRatingNarratives';
import { ExamTabProvider } from './components/tabs/ExamTabContext';
import HipsTab from './components/tabs/HipsTab';
import ShouldersTab from './components/tabs/ShouldersTab';
import ElbowsTab from './components/tabs/ElbowsTab';
import WristsTab from './components/tabs/WristsTab';
import GripTab from './components/tabs/GripTab';
import { gripParserData } from './utils/gripParserData';
import { identifierHints } from './utils/identifierHints';
import KneesTab from './components/tabs/KneesTab';
import AnklesTab from './components/tabs/AnklesTab';
import HandsTab from './components/tabs/HandsTab';
import AuthenticatedInput from './components/AuthenticatedInput';
import AuthenticatedSelect from './components/AuthenticatedSelect';
import { useToast } from './components/Toast';

// Admin-only; loaded on demand so regular users never download it.
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));

// Note: All calculation functions are defined in the file below.
// TypeScript hoists function declarations, so they're available throughout the file.
// No need for declare statements since the actual implementations exist.

// Cloud Functions base URL. A dev build can point at the emulator with
// REACT_APP_FUNCTIONS_URL=http://localhost:5015/qme-ratings-v2/us-central1.
// (This used to HEAD-probe a list of URLs before every run, adding a round trip.)
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-qme-ratings-v2.cloudfunctions.net';

// Main application component (without routing)
function MainApp() {
  // Authentication
  const { user, loading: authLoading, userProfile, incrementAIUsage, startManualSession, endManualSession } = useAuth();
  const { showToast } = useToast();
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // State for exam text input
  const [examText, setExamText] = useState<string>('');
  
  // Effect to initialize color scheme from localStorage or system preference
  useEffect(() => {
    const savedMode = localStorage.getItem('qme-dark-mode');
    if (savedMode !== null) {
      setDarkMode(savedMode === 'true');
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(prefersDark);
    }
  }, []);

  // Effect to handle scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300); // Show button after scrolling 300px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // State for the processed exam data - initialized with empty structure to display UI on load
  const [examData, setExamData] = useState<ExamData | null>(() => initializeExamData({}));
  
  // State for the active tab
  const [activeTab, setActiveTab] = useState<string>('full-exam');
  const [handSection, setHandSection] = useState<string>('thumbs');
  
  // Loading state for API calls
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // State for color scheme (dark/light mode)
  const [darkMode, setDarkMode] = useState<boolean>(true);

  // State for scroll-to-top button visibility
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);

  // State for app configuration
  const [appConfig, setAppConfig] = useState<{[key: string]: any}>({});

  /**
   * BODY REGION TABS CONFIGURATION
   * ===========================
   * 
   * This array defines all body region tabs in the UI.
   * When adding a new body region:
   * 
   * 1. Add a new tab entry with a unique ID and descriptive label
   * 2. Create corresponding tab content component below
   * 3. Update the tab content rendering logic in the JSX
   * 4. Ensure the ID matches any references in region detection code
   * 5. Add necessary impairment calculation functions for the region
   */
  const tabs = [
    { id: 'shoulders', label: 'Shoulders' },
    { id: 'elbows', label: 'Elbows' },
    { id: 'wrists', label: 'Wrists' },
    { id: 'hands', label: 'Hands' },
    { id: 'hips', label: 'Hips' },
    { id: 'knees', label: 'Knees' },
    { id: 'ankles', label: 'Ankles' },
    { id: 'grip', label: 'Grip' },
    // Gait is not rated: QME - Tool has no deterministic Table 17-5 calculator either.
  ];
  
  // State for detected regions
  const [processingStep, setProcessingStep] = useState<string>('');
  
  // State for clear confirmation (double-click to clear)
  const [clearConfirmRegion, setClearConfirmRegion] = useState<string | null>(null);
  const clearConfirmTimeout = useRef<NodeJS.Timeout | null>(null);
  const [clearAllConfirm, setClearAllConfirm] = useState(false);
  const clearAllConfirmTimeout = useRef<NodeJS.Timeout | null>(null);

  // Function to handle processing the exam text with robust error handling
  // Function to clear all values in the application
  const clearAllValues = () => {
    // Clear the exam text
    setExamText('');
    
    // Reset exam data to empty initialized state
    setExamData(initializeExamData({}));
    
    // Reset detected regions
    
    // Reset processing step
    setProcessingStep('');
    
    // Reset to first tab
    setActiveTab('full-exam');
    
    // No alert - silently clear values
  };

  const handleClearAll = () => {
    if (clearAllConfirm) {
      clearAllValues();
      setClearAllConfirm(false);
      if (clearAllConfirmTimeout.current) clearTimeout(clearAllConfirmTimeout.current);
    } else {
      setClearAllConfirm(true);
      if (clearAllConfirmTimeout.current) clearTimeout(clearAllConfirmTimeout.current);
      clearAllConfirmTimeout.current = setTimeout(() => setClearAllConfirm(false), 3000);
    }
  };

  const fillNormalValues = async (region: string) => {
    if (!user) { setShowAuthModal(true); return; }
    if (!userProfile) return;

    // Start session if needed (same pattern as AuthenticatedInput)
    if (!manualSessionActive(userProfile)) {
      const canStart = await startManualSession();
      if (!canStart) {
        showToast(`Monthly session limit reached.${userProfile.subscriptionTier === 'free' ? ' Upgrade to Premium for unlimited access.' : ''}`, { type: 'warning' });
        return;
      }
    }

    const normals = NORMAL_ROM_VALUES[region];
    if (!normals) return;

    // Hand digit regions are nested under examData.hands, and any digit's
    // button fills the whole hand (blank fields only on the other digits).
    if ((HAND_DIGITS as readonly string[]).includes(region)) {
      setExamData(prev => (prev ? { ...prev, hands: fillHandNormals(prev.hands, region) } : prev));
    } else {
      setExamData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [region]: { ...(prev as any)[region], ...normals }
        };
      });
    }
  };

  const clearRegionValues = (region: string) => {
    const emptyData = initializeExamData({});
    if (['thumbs', 'index_fingers', 'middle_fingers', 'ring_fingers', 'little_fingers'].includes(region)) {
      setExamData(prev => {
        if (!prev) return prev;
        return { ...prev, hands: { ...prev.hands, [region]: emptyData.hands[region as keyof typeof emptyData.hands] } };
      });
    } else {
      setExamData(prev => {
        if (!prev) return prev;
        return { ...prev, [region]: (emptyData as any)[region] };
      });
    }
  };

  const handleClearRegion = (region: string) => {
    if (clearConfirmRegion === region) {
      clearRegionValues(region);
      setClearConfirmRegion(null);
      if (clearConfirmTimeout.current) clearTimeout(clearConfirmTimeout.current);
    } else {
      setClearConfirmRegion(region);
      if (clearConfirmTimeout.current) clearTimeout(clearConfirmTimeout.current);
      clearConfirmTimeout.current = setTimeout(() => setClearConfirmRegion(null), 3000);
    }
  };

  const copyRegionValues = (region: string, direction: 'left_to_right' | 'right_to_left') => {
    const handDigits = ['thumbs', 'index_fingers', 'middle_fingers', 'ring_fingers', 'little_fingers'];
    const isHand = handDigits.includes(region);

    setExamData(prev => {
      if (!prev) return prev;
      const regionData = isHand ? { ...(prev.hands as any)[region] } : { ...(prev as any)[region] };
      const sourcePrefix = direction === 'left_to_right' ? 'left_' : 'right_';
      const targetPrefix = direction === 'left_to_right' ? 'right_' : 'left_';

      for (const key of Object.keys(regionData)) {
        if (key.startsWith(sourcePrefix)) {
          const targetKey = targetPrefix + key.slice(sourcePrefix.length);
          if (targetKey in regionData) {
            regionData[targetKey] = regionData[key];
          }
        }
      }

      if (isHand) {
        return { ...prev, hands: { ...prev.hands, [region]: regionData } };
      }
      return { ...prev, [region]: regionData };
    });
  };

  // One AI run at a time. ⌘↩ had no guard (only the button was disabled while
  // loading), so a key-repeat or double tap billed two runs and the slower one
  // landed after the doctor had started editing, silently overwriting the edits.
  const runInFlightRef = useRef(false);
  // Only the most recent run may write its results.
  const latestRunRef = useRef(0);

  const processExamText = async () => {
    if (runInFlightRef.current) return;
    if (!examText.trim()) {
      showToast('Please enter exam text to process', { type: 'warning' });
      return;
    }
    
    // Check authentication and increment AI usage
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    // Increment AI usage counter
    const canProceed = await incrementAIUsage();
    if (!canProceed) {
      showToast('You have reached your AI processing limit for this month. Please upgrade to Premium for unlimited access.', { type: 'warning' });
      return;
    }
    
    // Show loading state
    setIsLoading(true);
    setProcessingStep('Finding the body regions in the exam…');
    
    handleRealAPIProcessing();
  };
  
  // Real API processing using Gemini for all detection and parsing.
  // On any failure the exam already on screen (including hand edits) is left
  // untouched; only a successful run replaces it.
  const handleRealAPIProcessing = async () => {
    if (runInFlightRef.current) return;
    runInFlightRef.current = true;
    const runId = ++latestRunRef.current;
    const idToken = user ? await user.getIdToken().catch(() => null) : null;
    const authHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    };
    // detectRegions counts the run only after it succeeds; track that so the
    // failure messages say truthfully whether this run used a monthly session.
    let counted = false;
    const countNote = () => (counted
      ? ' This run did count toward your monthly AI limit.'
      : ' Your AI session was not counted.');
    const finish = () => { runInFlightRef.current = false; setIsLoading(false); setProcessingStep(''); };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      showToast(`Processing timed out.${countNote()} Please try again or use shorter exam text.`, { type: 'warning' });
      finish();
    }, 120000); // a 100 KB exam on cold functions can pass 60s (26 KB measured ~23s cold)

    const post = (endpoint: string) => fetch(`${FUNCTIONS_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ examText }),
      signal: controller.signal,
    });

    try {
      if (!examText || examText.trim().length === 0) {
        throw new Error('No exam text provided. Please enter medical report text.');
      }
      const MAX_TEXT_SIZE = 100 * 1024;
      if (examText.length > MAX_TEXT_SIZE) {
        throw new Error(`Exam text is too large (${Math.round(examText.length / 1024)}KB). Please limit to ${MAX_TEXT_SIZE / 1024}KB.`);
      }

      setProcessingStep('Finding the body regions in the exam…');
      const regionsResponse = await post('detectRegions');
      if (!regionsResponse.ok) {
        if (regionsResponse.status === 429) {
          clearTimeout(timeoutId);
          showToast('You have reached your monthly AI processing limit. Upgrade to Premium for unlimited access.', { type: 'warning' });
          finish();
          return;
        }
        if (regionsResponse.status === 401 || regionsResponse.status === 403) {
          throw new Error('Your sign-in has expired. Please sign out and back in');
        }
        throw new Error(`Error detecting regions: ${regionsResponse.status}`);
      }
      const regionsData = await regionsResponse.json();
      counted = !!regionsData.counted;
      const regions: Record<string, boolean> = regionsData.detectedRegions || {};

      setProcessingStep('Reading the exam…');
      const parsedData: any = {};
      // Regions detected but not parsed, so a dropped region is flagged rather
      // than silently rendered as normal / 0% impairment.
      const failedRegions: string[] = [];

      // One request per region. The hand is ONE call covering all five digits
      // (it used to be five calls, each sending the whole exam text).
      const parsers: { key: string; endpoint: string; label: string; apply: (d: any) => void }[] = [
        { key: 'grip', endpoint: 'parseGripOpenAI', label: 'grip', apply: (d) => { parsedData.grip_strength = gripParserData(d); } },
        { key: 'hands', endpoint: 'parseHandOpenAI', label: 'hands', apply: (d) => { parsedData.hands = d; } },
        { key: 'shoulders', endpoint: 'parseShoulderOpenAI', label: 'shoulders', apply: (d) => { parsedData.shoulders = d; } },
        { key: 'elbows', endpoint: 'parseElbowOpenAI', label: 'elbows', apply: (d) => { parsedData.elbows = d; } },
        { key: 'wrists', endpoint: 'parseWristOpenAI', label: 'wrists', apply: (d) => { parsedData.wrists = d; } },
        { key: 'hips', endpoint: 'parseHipOpenAI', label: 'hips', apply: (d) => { parsedData.hips = d; } },
        { key: 'knees', endpoint: 'parseKneeOpenAI', label: 'knees', apply: (d) => { parsedData.knees = d; } },
        { key: 'ankles', endpoint: 'parseAnkleOpenAI', label: 'ankles', apply: (d) => { parsedData.ankles = d; } },
      ];

      // Name what was found and count it off, instead of one static line for
      // the 15-35 seconds this takes.
      const active = parsers.filter(({ key }) => regions[key]);
      let done = 0;
      const progress = () => setProcessingStep(
        `Reading ${active.map((p) => p.label).join(', ')}… ${done} of ${active.length} done`);
      if (active.length) progress();
      await Promise.allSettled(
        active.map(async ({ endpoint, label, apply }) => {
          try {
            const resp = await post(endpoint);
            if (!resp.ok) { failedRegions.push(label); return; }
            const data = await resp.json();
            apply(data.examData || data.data || {});
          } catch {
            failedRegions.push(label);
          } finally {
            done++;
            if (!controller.signal.aborted) progress();
          }
        })
      );

      // The timeout already fired and reported, or a newer run exists: don't
      // overwrite anything.
      if (controller.signal.aborted || runId !== latestRunRef.current) return;
      clearTimeout(timeoutId);

      if (failedRegions.length > 0) {
        showToast(
          `Couldn't parse: ${failedRegions.join(', ')}. These regions may be incomplete — please review or re-run.`,
          { type: 'error', durationMs: 9000 }
        );
      }
      if (Object.keys(parsedData).length === 0) {
        if (failedRegions.length === 0) {
          showToast('No exam measurements were found in that text.', { type: 'warning' });
        }
        finish();
        return;
      }

      const initializedData = initializeExamData(parsedData);
      setExamData(initializedData);
      setActiveTab(findFirstTabWithData(initializedData));
      scrollToResults();
      finish();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (controller.signal.aborted) return; // the timeout handler already reported
      console.error('Error processing exam text:', error?.message);
      showToast(`Failed to process exam text: ${error.message}.${countNote()}`, { type: 'error' });
      finish();
    }
  };

  
  // Function to toggle between dark and light mode
  const toggleColorScheme = () => {
    const newMode: boolean = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('qme-dark-mode', String(newMode));
    
    // Apply light mode class to HTML element (dark is default via CSS vars)
    if (newMode) {
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
    }
  };
  
  // Scroll to top function
  const scrollToTop = () => {
    const examInput = document.getElementById('examInput');
    if (examInput) {
      examInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      examInput.focus();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Function to find the first tab that contains data - matches Results Summary order
  const findFirstTabWithData = (examData: ExamData | null): string => {
    if (!examData) {
      return tabs[0].id;
    }


    // Check in the same order as Results Summary narratives are generated
    // 1. Shoulders
    if (examData.shoulders && hasShoulderData(examData.shoulders)) {
      return 'shoulders';
    }
    
    // 2. Elbows
    if (examData.elbows && hasElbowData(examData.elbows)) {
      return 'elbows';
    }
    
    // 3. Wrists
    if (examData.wrists && hasWristData(examData.wrists)) {
      return 'wrists';
    }
    
    // 4. Hands
    if (examData.hands && hasHandData(examData.hands)) {
      return 'hands';
    }
    
    // 5. Grip (part of hands tab)
    if (examData.grip_strength && hasGripData(examData.grip_strength)) {
      return 'hands'; // Grip narratives appear in results but hands tab covers grip
    }
    
    // 6. Hips
    if (examData.hips && hasHipData(examData.hips)) {
      return 'hips';
    }
    
    // 7. Knees
    if (examData.knees && hasKneeData(examData.knees)) {
      return 'knees';
    }
    
    // 8. Ankles
    if (examData.ankles && hasAnkleData(examData.ankles)) {
      return 'ankles';
    }
    
    // If no specific tab has data, return the first available tab as default
    return tabs[0].id;
  };

  // Helper functions to check if each body region has data
  const hasShoulderData = (shoulders: any): boolean => {
    return shoulders.right_flexion !== null || shoulders.left_flexion !== null ||
           shoulders.right_extension !== null || shoulders.left_extension !== null ||
           shoulders.right_abduction !== null || shoulders.left_abduction !== null ||
           shoulders.right_adduction !== null || shoulders.left_adduction !== null ||
           shoulders.right_internal_rotation !== null || shoulders.left_internal_rotation !== null ||
           shoulders.right_external_rotation !== null || shoulders.left_external_rotation !== null;
  };

  const hasElbowData = (elbows: any): boolean => {
    return elbows.right_flexion !== null || elbows.left_flexion !== null ||
           elbows.right_extension !== null || elbows.left_extension !== null ||
           elbows.right_pronation !== null || elbows.left_pronation !== null ||
           elbows.right_supination !== null || elbows.left_supination !== null;
  };

  const hasWristData = (wrists: any): boolean => {
    return wrists.right_flexion !== null || wrists.left_flexion !== null ||
           wrists.right_extension !== null || wrists.left_extension !== null ||
           wrists.right_radial_deviation !== null || wrists.left_radial_deviation !== null ||
           wrists.right_ulnar_deviation !== null || wrists.left_ulnar_deviation !== null;
  };

  // Any recorded value on any digit. (The hand-written field list this replaced
  // used names that don't exist, e.g. right_mp_flexion for right_mcp_flexion.)
  const hasHandData = (hands: any): boolean =>
    !!hands && Object.values(hands).some((digit: any) =>
      !!digit && Object.values(digit).some((v) => v !== null && v !== undefined && v !== ''));

  const hasHipData = (hips: any): boolean => {
    return hips.right_flexion !== null || hips.left_flexion !== null ||
           hips.right_extension_contracture !== null || hips.left_extension_contracture !== null ||
           hips.right_internal_rotation !== null || hips.left_internal_rotation !== null ||
           hips.right_external_rotation !== null || hips.left_external_rotation !== null ||
           hips.right_adduction !== null || hips.left_adduction !== null ||
           hips.right_abduction_contracture !== null || hips.left_abduction_contracture !== null;
  };

  const hasKneeData = (knees: any): boolean => {
    return knees.right_flexion !== null || knees.left_flexion !== null ||
           knees.right_extension !== null || knees.left_extension !== null ||
           knees.right_flexion_contracture_passive !== null || knees.left_flexion_contracture_passive !== null ||
           knees.right_extensor_lag !== null || knees.left_extensor_lag !== null ||
           knees.right_varus !== null || knees.left_varus !== null ||
           knees.right_valgus !== null || knees.left_valgus !== null;
  };

  const hasAnkleData = (ankles: any): boolean => {
    const hasData = ankles.right_dorsiflexion !== null || ankles.left_dorsiflexion !== null ||
           ankles.right_plantar_flexion !== null || ankles.left_plantar_flexion !== null ||
           ankles.right_inversion !== null || ankles.left_inversion !== null ||
           ankles.right_eversion !== null || ankles.left_eversion !== null;
    return hasData;
  };

  // Grip attempts are right_attempt_1..3 / left_attempt_1..3 (this used to look
  // for right_grip_1, which doesn't exist, so a grip-only exam opened on Shoulders).
  const hasGripData = (grip: any): boolean =>
    !!grip && [1, 2, 3].some((i) =>
      [grip[`right_attempt_${i}`], grip[`left_attempt_${i}`]].some((v) => v !== null && v !== undefined && v !== ''));

  // Function to scroll to the Results Summary box after processing
  const scrollToResults = () => {
    // Use a longer delay to ensure any auto-tab scrolling completes first
    setTimeout(() => {
      
      // Look for the Results Summary box
      const resultsBox = document.getElementById('results-summary-box');
      if (resultsBox) {
        resultsBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback: look for any results-related element
        const fallbackElement = document.querySelector('.results, [data-testid="results"], h2');
        if (fallbackElement) {
          fallbackElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // Final fallback: scroll to a reasonable position (below the input area)
          window.scrollTo({ top: 800, behavior: 'smooth' });
        }
      }
    }, 100); // Minimal delay for immediate response while ensuring DOM updates complete
  };

  // Apply initial light mode class (dark is default via CSS vars)
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
    }
  }, [darkMode]);
  
  
  // Shared state/handlers for extracted tab components (see ExamTabContext)
  const examTabContextValue = {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
    handSection, setHandSection,
  };

  // Return the App JSX
  return (
    <ExamTabProvider value={examTabContextValue}>
    <div className="min-h-screen bg-surface text-content transition-colors duration-300">
      {/* User Authentication Header */}
      {user ? (
        <UserHeader 
          user={user} 
          onSignInClick={() => setShowAuthModal(true)}
          usageData={userProfile ? {
            aiProcessingCount: userProfile.usage.aiProcessingCount,
            manualSessionCount: userProfile.usage.manualSessionCount,
            subscriptionTier: userProfile.subscriptionTier
          } : undefined}
        />
      ) : (
        <div className="bg-surface-raised border-b border-border px-4 py-2.5">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <span className="text-accent-400 font-bold text-lg tracking-tight">QME</span>
              <span className="text-content-tertiary text-sm hidden sm:inline">Ratings</span>
            </div>
            <button
              onClick={() => setShowAuthModal(true)}
              className="btn-primary text-sm px-4 py-1.5"
            >
              Sign In
            </button>
          </div>
        </div>
      )}

      {/* Header Section */}
      <header className="py-8 px-4 relative">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-content">
              <span className="text-accent-400">QME</span> Ratings
            </h1>
            <p className="text-content-tertiary text-sm mt-0.5">AMA Guides 5th Edition Impairment Calculator</p>
          </div>

          {/* Dark/Light Mode Toggle Button */}
          <button
            onClick={toggleColorScheme}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2.5 rounded-xl bg-surface-raised border border-border hover:border-border-subtle transition-colors"
          >
            {darkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Exam Input Section */}
      <section className="px-4">
        <div className="max-w-3xl mx-auto card p-6 transition-colors">
          <textarea
            id="examInput"
            name="examInput"
            rows={8}
            placeholder={user ? "Paste full narrative physical exam here… (Command+Return to process)" : "Please sign in to enter exam data"}
            className={`input-field w-full resize-y p-4 font-mono leading-relaxed text-left ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Physical exam text input"
            value={examText}
            onChange={(e) => user && setExamText(e.target.value)}
            onKeyDown={(e) => {
              // Check for Command+Return (Mac) or Ctrl+Enter (Windows/Linux)
              if (user && (e.metaKey || e.ctrlKey) && (e.key === 'Enter') && !e.repeat) {
                e.preventDefault();
                processExamText();
              }
            }}
            disabled={!user}
          />
          <p className="mt-2 text-sm text-content-secondary">Paste physical exam findings only. Remove patient names, dates of birth, SSNs and claim numbers first.</p>
          {identifierHints(examText).length > 0 && (
            <p role="status" className="mt-2 rounded border border-amber-500/60 bg-amber-500/10 p-3 text-sm text-content">
              Possible identifiers found: {identifierHints(examText).join(', ')}. Remove identifiers before processing.
            </p>
          )}
          <div className="flex flex-col sm:flex-row justify-between w-full">
            <button
              id="processBtn"
              onClick={user ? processExamText : () => setShowAuthModal(true)}
              className={`mt-4 w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center ${
                user
                  ? 'btn-primary'
                  : 'bg-surface-overlay text-content-secondary hover:bg-surface-raised'
              }`}
              aria-label={user ? "Process exam text with AI" : "Sign in to process exam text"}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : user ? (
                <>Process Physical Exam <span className="text-xs opacity-70 ml-1 align-middle">(⌘↩)</span></>
              ) : (
                <>Sign In to Process Exam</>
              )}
            </button>
            {user && (
              <button
                id="clearAllBtn"
                onClick={handleClearAll}
                className={`mt-4 w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg flex items-center justify-center transition-colors ${clearAllConfirm ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-content-tertiary hover:text-red-400 hover:bg-red-500/10'}`}
                aria-label="Clear all values"
              >
                {clearAllConfirm ? 'Confirm Clear All?' : 'Clear All Values'}
              </button>
            )}
          </div>
          {isLoading && processingStep && (
            <p className="mt-3 text-sm text-content-secondary text-center sm:text-left" role="status" aria-live="polite">
              {processingStep}
            </p>
          )}
        </div>
      </section>

      {/* Results and Accordions - always visible */}
        <section className="mt-8 mb-24 px-4"> {/* Added margin bottom for footer clearance */}
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 mb-4">
              <h2 className="text-2xl font-bold">Results & Output</h2>
              {/* The edit tabs sit below the (long) physical exam; jump links save
                  scrolling past ~2,000px of tables to correct one value. */}
              <nav aria-label="Jump to section" className="flex gap-5 text-sm font-sans -my-2.5">
                <a href="#physical-exam" className="inline-block py-2.5 text-content-secondary hover:text-accent-400 underline-offset-4 hover:underline">Physical exam ↓</a>
                <a href="#edit-values" className="inline-block py-2.5 text-content-secondary hover:text-accent-400 underline-offset-4 hover:underline">Edit values ↓</a>
              </nav>
            </div>
            
            {/* Results Summary Box */}
            <ResultsSummaryBox examData={examData} darkMode={darkMode} />

            {/* Copy/paste-ready physical exam */}
            <PhysicalExamBox examData={examData} />

            {/* Other accordions removed as requested */}
          </div>
        </section>

      {/* Tab Navigation - always visible */}
        <div id="edit-values" className="mt-8 px-4 mb-24 scroll-mt-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold mb-1">Review &amp; edit values</h2>
            <p className="text-content-tertiary text-sm mb-4 font-sans">Changes here update the ratings and the physical exam above.</p>
            {/* Tab Headers */}
            <div className="flex overflow-x-auto gap-1 sticky top-0 bg-surface pt-2 pb-0 z-10 border-b border-border" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`px-4 py-2.5 font-medium transition-colors whitespace-nowrap border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'border-accent-500 text-accent-400'
                      : 'border-transparent text-content-tertiary hover:text-content-secondary hover:border-border'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`${tab.id}-panel`}
                  id={`${tab.id}-tab`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-surface-raised rounded-b-xl border border-t-0 border-border p-6 transition-colors">


              
              
              {/* Shoulder Tab */}
              {activeTab === 'shoulders' && <ShouldersTab />}

              {/* Elbows Tab */}
              {activeTab === 'elbows' && <ElbowsTab />}

              {/* Wrists Tab */}
              {activeTab === 'wrists' && <WristsTab />}


              {/* Hands Tab */}
              {activeTab === 'hands' && <HandsTab />}

              {/* Grip Tab */}
              {activeTab === 'grip' && <GripTab />}
              
              

              {/* Hips Tab */}
              {activeTab === 'hips' && <HipsTab />}

              {/* Knees Tab */}
              {activeTab === 'knees' && <KneesTab />}

              {/* Ankles Tab */}
              {activeTab === 'ankles' && <AnklesTab />}

              {/* Gait Tab */}
              
            </div>
          </div>
        </div>

      {/* Bottom bar removed as per user request - only using the main Process Physical Exam button */}

      {/* Floating Scroll-to-Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-6 right-6 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-50 ${
            darkMode 
              ? 'bg-accent-600 hover:bg-accent-500 text-white' 
              : 'bg-accent-600 hover:bg-accent-500 text-white'
          }`}
          aria-label="Scroll to top and focus input"
          title="Scroll to top"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            className="w-6 h-6"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        </button>
      )}

      <footer className="px-4 py-6 text-center text-sm text-content-secondary">
        <a className="underline" href="/terms.html" target="_blank" rel="noreferrer">Terms</a>{' · '}
        <a className="underline" href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>{' · '}
        Not medical or legal advice.
      </footer>
      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => setShowAuthModal(false)}
      />
    </div>
    </ExamTabProvider>
  );
}

// Router wrapper component
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admin" element={<React.Suspense fallback={null}><AdminDashboard /></React.Suspense>} />
        <Route path="/" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

/**
 * BODY REGION RATING CALCULATION FUNCTIONS
 * =====================================
 * 
 * These functions convert ROM measurements to impairment percentages based on AMA Guides.
 * When implementing calculations for a new body region:
 * 
 * 1. Create similar functions for each measurement in the region
 * 2. Use anchor points from AMA Guides with interpolation between points
 * 3. Handle null/undefined values appropriately
 * 4. Ensure proper bounds checking for min/max values
 * 5. For upper extremities, convert to WPI by multiplying by 0.6 before display, for lower extremity multiply by 0.4.
 */

/**
 * Calculates grip strength impairment rating based on the difference between tested and non-tested sides
 * @param gripData - Grip strength measurements for both right and left hands
 * @param side - The side being evaluated ('right' or 'left')
 * @returns Upper extremity impairment percentage (before WPI conversion)
 */
export default App;
